/**
 * Deliver through the Electron native bridge when available, then the
 * installed application's service worker, and finally the page Notification
 * API. We deliberately do not treat
 * Notification.permission as authoritative: app wrappers can expose a stale
 * value even when macOS delivery is enabled. The actual delivery attempt is
 * the source of truth.
 */
export type AppNotificationOptions = NotificationOptions & { renotify?: boolean };

interface ALGDesktopNotificationOptions {
  title: string;
  body?: string;
  tag?: string;
  silent?: boolean;
  requireInteraction?: boolean;
  url?: string;
}

interface ALGDesktopNotificationResult {
  ok: boolean;
  error?: string;
  tag?: string | null;
  closed?: boolean;
}

declare global {
  interface Window {
    ALGDesktop?: {
      showNotification?(
        options: ALGDesktopNotificationOptions,
      ): ALGDesktopNotificationResult | Promise<ALGDesktopNotificationResult>;
      closeNotification?(
        tag: string,
      ): ALGDesktopNotificationResult | Promise<ALGDesktopNotificationResult>;
    };
  }
}

export type NotificationDelivery = {
  channel: "desktop-native" | "application-service" | "page";
  verified: boolean;
  notification?: Notification;
};

function sameOriginNotificationUrl(options: AppNotificationOptions): string | undefined {
  const candidate = (options.data as { url?: unknown } | null | undefined)?.url;
  if (typeof candidate !== "string") return undefined;
  try {
    const url = new URL(candidate, window.location.href);
    return url.origin === window.location.origin ? url.href : undefined;
  } catch {
    return undefined;
  }
}

export async function deliverSystemNotification(
  title: string,
  options: AppNotificationOptions,
  onPageClick?: (notification: Notification) => void,
): Promise<NotificationDelivery> {
  const nativeNotifications = window.ALGDesktop;
  if (nativeNotifications?.showNotification) {
    try {
      const result = await nativeNotifications.showNotification({
        title,
        body: options.body,
        tag: options.tag,
        silent: options.silent === true,
        requireInteraction: options.requireInteraction === true,
        url: sameOriginNotificationUrl(options),
      });
      if (!result?.ok) throw new Error(result?.error || "native notification failed");
      return { channel: "desktop-native", verified: true };
    } catch {
      // The existing service-worker/page paths remain the browser and failure fallback.
    }
  }

  let serviceWorkerError: unknown = null;
  if ("serviceWorker" in navigator) {
    try {
      const registration = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("application notification service is not ready")), 1500),
        ),
      ]);
      await registration.showNotification(title, options);
      if (options.tag) {
        const created = await registration.getNotifications({ tag: options.tag });
        if (!created.some((notification) => notification.tag === options.tag)) {
          throw new Error("application accepted the request but did not create the notification");
        }
      }
      return { channel: "application-service", verified: Boolean(options.tag) };
    } catch (error) {
      serviceWorkerError = error;
    }
  }

  if (typeof Notification === "undefined") {
    throw serviceWorkerError ?? new Error("system notification API is unavailable");
  }
  try {
    const notification = new Notification(title, options);
    if (onPageClick) notification.onclick = () => onPageClick(notification);
    return { channel: "page", verified: false, notification };
  } catch (error) {
    throw serviceWorkerError ?? error;
  }
}
